import React from 'react';
import { Card, Table, Alert, Row, Col } from 'antd';
import { useOwnerGrowthProfit } from '@/hooks/useOwnerIntelligence';
import { useQuery } from '@tanstack/react-query';
import ownerIntelligenceService from '@/services/ownerIntelligence.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { PrimaryKpiCard, OiPageFrame, OiMetricHelp } from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { OiAreaTrendChart, OiWaterfallChart, OiHorizontalBarChart } from '@/components/owner-intelligence/charts/OiCharts';

const GrowthProfitPage: React.FC = () => {
  const { data, isLoading, error } = useOwnerGrowthProfit();
  const { data: benchmarks } = useQuery({
    queryKey: ['owner-intelligence', 'benchmarks'],
    queryFn: () => ownerIntelligenceService.getBenchmarks(),
  });
  const { formatCurrency } = useCurrency();

  const yoy = data?.revenue_ytd_vs_ly;
  const margin = data?.margin_summary;
  const bridge = data?.margin_bridge;

  const monthlyChart = (data?.monthly_trend ?? []).map((m) => ({
    label: m.monthLabel,
    revenue: m.revenue,
  }));

  const waterfallSteps = bridge
    ? [
        { label: 'Revenue', value: bridge.revenue },
        { label: 'Cost', value: -bridge.estimated_cost },
        { label: 'Gross profit', value: bridge.gross_profit },
      ]
    : [];

  const teamChart = (data?.revenue_by_team ?? []).map((t) => ({
    name: t.team_name,
    pipeline: t.pipeline_value,
    booked: t.booked_sales_order_value,
  }));

  const benchCustomers = (benchmarks as { customers?: Array<{ customer_name: string; revenue_ytd: number; rank: number }> })?.customers ?? [];

  return (
    <OiPageFrame
      subtitle="Revenue trends, margins, profitability, and YoY benchmarks."
      loading={isLoading && !data}
      error={Boolean(error)}
      errorMessage="Growth & profit data unavailable"
    >
      {data ? (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}>
              <PrimaryKpiCard label="Revenue MTD" value={formatCurrency(Number(data.revenue_mtd ?? 0))} />
            </Col>
            <Col xs={12} sm={6}>
              <PrimaryKpiCard label="Revenue YTD" value={formatCurrency(Number(data.revenue_ytd ?? 0))} />
            </Col>
            <Col xs={12} sm={6}>
              <PrimaryKpiCard
                label="YoY variance"
                value={yoy?.variance_pct != null ? `${yoy.variance_pct.toFixed(1)}%` : '—'}
                deltaPct={yoy?.variance_pct}
              />
            </Col>
            <Col xs={12} sm={6}>
              <PrimaryKpiCard
                label="Gross margin %"
                value={margin?.gross_margin_pct != null ? `${margin.gross_margin_pct.toFixed(1)}%` : '—'}
              />
            </Col>
          </Row>

          <Card title="Monthly revenue trend" style={{ marginBottom: 16 }} bordered={false}>
            <OiAreaTrendChart data={monthlyChart} dataKey="revenue" xKey="label" height={280} />
            <Table
              size="small"
              pagination={false}
              rowKey="monthLabel"
              style={{ marginTop: 16 }}
              dataSource={data.monthly_trend ?? []}
              columns={[
                { title: 'Month', dataIndex: 'monthLabel' },
                { title: 'Revenue', dataIndex: 'revenue', render: (v: number) => formatCurrency(v) },
              ]}
            />
          </Card>

          {bridge ? (
            <Card
              title={
                <span>
                  Margin bridge (YTD estimate){' '}
                  <OiMetricHelp title="Margin bridge">
                    Revenue minus estimated cost from loaded TRE/overhead data. Treat as directional — not audited margin.
                  </OiMetricHelp>
                </span>
              }
              style={{ marginBottom: 16 }}
              bordered={false}
            >
              <OiWaterfallChart steps={waterfallSteps} height={220} />
              {bridge.margin_leakage_hint ? (
                <Alert type="warning" message={bridge.margin_leakage_hint} style={{ marginTop: 12 }} showIcon />
              ) : null}
            </Card>
          ) : null}

          <Card title="Revenue by sales team" style={{ marginBottom: 16 }} bordered={false}>
            <OiHorizontalBarChart data={teamChart} dataKey="pipeline" nameKey="name" height={240} />
            <Table
              size="small"
              rowKey="team_id"
              style={{ marginTop: 16 }}
              dataSource={data.revenue_by_team ?? []}
              columns={[
                { title: 'Team', dataIndex: 'team_name' },
                { title: 'Pipeline', dataIndex: 'pipeline_value', render: (v: number) => formatCurrency(v) },
                { title: 'Quotation', dataIndex: 'quotation_value', render: (v: number) => formatCurrency(v) },
                { title: 'Booked SO', dataIndex: 'booked_sales_order_value', render: (v: number) => formatCurrency(v) },
              ]}
            />
          </Card>

          <Card title="Top customers by revenue (YTD)" style={{ marginBottom: 16 }} bordered={false}>
            <Table
              size="small"
              rowKey="customer_id"
              dataSource={data.top_customers ?? []}
              columns={[
                { title: 'Customer', dataIndex: 'customer_name' },
                { title: 'Revenue', dataIndex: 'revenue', render: (v: number) => formatCurrency(v) },
                { title: 'Invoices', dataIndex: 'invoice_count' },
              ]}
            />
          </Card>

          <Card id="benchmarks" title="YoY benchmarks" bordered={false}>
            <Table
              size="small"
              rowKey="customer_name"
              dataSource={benchCustomers}
              locale={{ emptyText: 'Benchmark data loads with sales history' }}
              columns={[
                { title: 'Rank', dataIndex: 'rank' },
                { title: 'Customer', dataIndex: 'customer_name' },
                { title: 'Revenue YTD', dataIndex: 'revenue_ytd', render: (v: number) => formatCurrency(v) },
              ]}
            />
          </Card>
        </>
      ) : null}
    </OiPageFrame>
  );
};

export default GrowthProfitPage;
